import { X, Download, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * CertificateViewer — full-screen overlay that displays the donation
 * receipt PDF *inside the app*, with an obvious X close button.
 *
 * Why a wrapper (and not just <a target="_blank">)?
 *   • The native browser PDF viewer has its own chrome, no way to
 *     return to the previous screen except the back arrow — donors
 *     and admins kept getting stranded with "no way out" of the
 *     receipt, which is the bug we're fixing.
 *   • Embedding the PDF in an iframe inside an in-app overlay lets
 *     us render an X (and a Hebrew "סגירה" label) in the corner.
 *     ESC closes too, mobile gets a thumb-friendly hit target.
 *
 * The PDF itself is unchanged — same backend endpoint, same template,
 * same Cache-Control. We only wrap the *presentation* of the PDF.
 */
export default function CertificateViewer({
  open,
  url,
  onClose,
  testid = "certificate-viewer",
}) {
  // Lock background scroll while the modal is open so a long admin
  // table doesn't jitter under the overlay on iOS.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close — standard modal expectation, free for keyboard
  // users.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // The iframe occasionally fails to start the load on cold cache.
  // Track that so we can show a fallback button rather than a blank
  // square. (The user can still hit "פתח ב-tab חדש" and view it.)
  const [iframeReady, setIframeReady] = useState(false);
  useEffect(() => { if (open) setIframeReady(false); }, [open, url]);

  if (!open || !url) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="תעודת תרומה"
      data-testid={testid}
      className="fixed inset-0 z-[120] flex flex-col bg-black/85 backdrop-blur-sm"
      dir="rtl"
    >
      {/* Top toolbar — close on the left (RTL), download + external on the right. */}
      <div className="flex items-center justify-between gap-3 px-3 sm:px-5 py-2.5 bg-neutral-900/90 border-b border-gold/20 text-cream">
        <button
          type="button"
          onClick={onClose}
          aria-label="סגירה"
          data-testid={`${testid}-close`}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm tracking-wide hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gold/60"
        >
          <X className="h-5 w-5" strokeWidth={1.8} />
          <span className="hidden sm:inline">סגירה</span>
        </button>

        <span className="font-heading text-sm tracking-[0.2em] uppercase text-gold/80">
          תעודת תרומה · CERTIFICATE
        </span>

        <div className="flex items-center gap-1.5">
          <a
            href={url}
            download
            data-testid={`${testid}-download`}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-white/10"
            title="הורדת PDF"
          >
            <Download className="h-4 w-4" strokeWidth={1.6} />
            <span className="hidden sm:inline">הורדה</span>
          </a>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-external`}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-white/10"
            title="פתיחה בכרטיסייה חדשה"
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.6} />
          </a>
        </div>
      </div>

      {/* PDF area. We use #toolbar=0 to hide the duplicate Chrome
          viewer toolbar where possible. iOS Safari ignores the hash
          and just renders the document. */}
      <div className="relative flex-1 bg-neutral-950">
        {!iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center text-cream/70 text-sm">
            טוען תעודה…
          </div>
        )}
        <iframe
          title="תעודת תרומה"
          src={`${url}#toolbar=0&view=FitH`}
          className="w-full h-full"
          onLoad={() => setIframeReady(true)}
        />
      </div>
    </div>
  );
}
